import styled from 'foundations-theming/utils';
import TruncateDiv from './TruncateDiv';
import { useMemo } from 'react';
import { memo } from 'react';
import { useIsIdle } from '../hooks/useIsIdle';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { Avatar } from 'avatar-components/Avatar';
import { CONTACT_TYPE_ID, COMPANY_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
const DefaultLinkComponent = ({
  href,
  role,
  onClick,
  children
}) => {
  return /*#__PURE__*/_jsx(Link, {
    href: href,
    role: role,
    onClick: onClick,
    children: children
  });
};
const AvatarContainer = styled.div.withConfig({
  displayName: "AvatarCell__AvatarContainer"
})(["height:24px;width:24px;"]);
const AvatarPlaceholder = styled.div.withConfig({
  displayName: "AvatarCell__AvatarPlaceholder"
})(["background-color:", ";border-color:transparent;border-radius:50%;width:100%;height:100%;"], ({
  theme
}) => theme.color['border-core-pressed']);
/**
 * This component is meant to render an avatar around "children" you pass in.
 * It doesnt render a link, you have to do that on your own.
 *
 * ONLY CONTACT_TYPE_ID and COMPANY_TYPE_ID have avatars. Please only use this when the objectTypeId is CONTACT_TYPE_ID or COMPANY_TYPE_ID
 *
 * You should use this if you want the little avatar circle, but you want to manage most everything else
 * If you want something that gets you the "suggested" experience and you dont need any customization, use "AvatarCell" instead
 */
export const AvatarCellWrapper = ({
  displayName,
  id,
  objectTypeId,
  isHubSpotUserEmail = false,
  children
}) => {
  const type = objectTypeId === CONTACT_TYPE_ID ? 'vid' : 'companyId';
  const isIdle = useIsIdle();
  const lookupProp = useMemo(() => {
    if (isHubSpotUserEmail) {
      return {
        hubSpotUserEmail: id
      };
    }
    return {
      [type]: Number(id)
    };
  }, [id, type, isHubSpotUserEmail]);
  const avatarType = isHubSpotUserEmail ? 'hubSpotUserEmail' : type;
  const avatar = isIdle && id ? /*#__PURE__*/_jsx(Avatar, Object.assign({
    type: avatarType
  }, lookupProp, {
    displayName: displayName,
    size: "xs"
  })) : /*#__PURE__*/_jsx(AvatarPlaceholder, {
    "data-test-id": "avatar-placeholder"
  });
  return /*#__PURE__*/_jsx(MediaObject, {
    align: "center",
    itemLeft: id && avatar,
    MediaLeft: AvatarContainer,
    children: /*#__PURE__*/_jsx(TruncateDiv, {
      children: children
    })
  });
};

/**
 * This component will take in props and then render an avatar and link
 *
 * Its an opinionated easy to use component - if you find it doesnt work for you, consider using AvatarCellWrapper which gives you more control
 */
export const AvatarCell = ({
  // default to link component here as well in case this component is called externally
  // so that it matches the expected behavior
  LinkComponent = DefaultLinkComponent,
  type,
  id,
  href,
  displayName,
  onClick,
  objectTypeId,
  children
}) => {
  // Determine which objectTypeId to use based on which prop is provided
  const {
    resolvedObjectTypeId,
    isHubSpotUserEmail
  } = useMemo(() => {
    if (objectTypeId) {
      return {
        resolvedObjectTypeId: objectTypeId,
        isHubSpotUserEmail: false
      };
    }
    if (type === 'vid') {
      return {
        resolvedObjectTypeId: CONTACT_TYPE_ID,
        isHubSpotUserEmail: false
      };
    }
    if (type === 'companyId') {
      return {
        resolvedObjectTypeId: COMPANY_TYPE_ID,
        isHubSpotUserEmail: false
      };
    }
    if (type === 'hubSpotUserEmail') {
      return {
        resolvedObjectTypeId: CONTACT_TYPE_ID,
        isHubSpotUserEmail: true
      };
    }
    // Default fallback
    return {
      resolvedObjectTypeId: CONTACT_TYPE_ID,
      isHubSpotUserEmail: false
    };
  }, [type, objectTypeId]);
  const isClickable = href || onClick;
  return /*#__PURE__*/_jsx(AvatarCellWrapper, {
    displayName: displayName,
    id: id,
    objectTypeId: resolvedObjectTypeId,
    isHubSpotUserEmail: isHubSpotUserEmail,
    children: isClickable ? /*#__PURE__*/_jsx(LinkComponent, {
      role: "link",
      href: href,
      onClick: onClick,
      children: children
    }) : children
  });
};
export default /*#__PURE__*/memo(AvatarCell);